import { useState, useEffect } from 'react'
import { paymentsApi, type Invoice } from '../../api/payments'
import { useUiStore } from '../../stores/uiStore'

interface Props {
  onBack: () => void
}

function statusColor(status: Invoice['status']): string {
  switch (status) {
    case 'paid': return 'text-green-400'
    case 'expired': return 'text-txt-meta line-through'
    default: return 'text-yellow-400'
  }
}

function statusLabel(status: Invoice['status']): string {
  switch (status) {
    case 'paid': return 'Paid'
    case 'expired': return 'Expired'
    default: return 'Pending'
  }
}

export function InvoicesPanel({ onBack }: Props) {
  const [invoices, setInvoices] = useState<Invoice[]>([])
  const [loading, setLoading] = useState(true)
  const [paying, setPaying] = useState<string | null>(null)
  const { addToast } = useUiStore()

  useEffect(() => {
    paymentsApi.listInvoices()
      .then((r) => setInvoices(r.invoices ?? []))
      .catch(() => addToast('error', 'Не удалось загрузить счета'))
      .finally(() => setLoading(false))
  }, [addToast])

  const handlePay = async (invoice: Invoice) => {
    setPaying(invoice.id)
    try {
      await paymentsApi.payInvoice(invoice.id)
      setInvoices((prev) => prev.map((inv) => inv.id === invoice.id ? { ...inv, status: 'paid' } : inv))
      addToast('success', `Paid ${invoice.price_stars} ⭐`)
    } catch {
      addToast('error', 'Ошибка оплаты')
    } finally {
      setPaying(null)
    }
  }

  const pending = invoices.filter((i) => i.status === 'pending')
  const other = invoices.filter((i) => i.status !== 'pending')

  return (
    <div className="flex flex-col h-full bg-bg-primary">
      <div className="flex items-center gap-3 px-4 py-3 border-b border-divider shrink-0">
        <button onClick={onBack} className="text-txt-secondary hover:text-txt-primary transition-colors">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M19 12H5M12 5l-7 7 7 7"/>
          </svg>
        </button>
        <h2 className="text-txt-primary font-semibold text-base flex-1">💳 Invoices</h2>
        {pending.length > 0 && (
          <span className="bg-yellow-500/20 text-yellow-400 text-xs px-2 py-0.5 rounded-full font-medium">
            {pending.length} pending
          </span>
        )}
      </div>

      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="flex justify-center py-8">
            <div className="w-5 h-5 border-2 border-accent/30 border-t-accent rounded-full animate-spin" />
          </div>
        ) : invoices.length === 0 ? (
          <div className="text-center py-10 px-4">
            <p className="text-4xl mb-2">💳</p>
            <p className="text-txt-primary font-medium">No invoices</p>
            <p className="text-txt-meta text-sm mt-1">Invoices from bots will appear here</p>
          </div>
        ) : (
          <>
            {pending.length > 0 && (
              <div className="mt-4">
                <p className="text-txt-meta text-xs uppercase tracking-wider font-medium px-4 mb-2">Pending</p>
                <div className="space-y-2 px-4">
                  {pending.map((inv) => (
                    <InvoiceCard key={inv.id} invoice={inv} paying={paying === inv.id} onPay={() => handlePay(inv)} />
                  ))}
                </div>
              </div>
            )}
            {other.length > 0 && (
              <div className="mt-4 mb-4">
                <p className="text-txt-meta text-xs uppercase tracking-wider font-medium px-4 mb-2">History</p>
                <div className="space-y-2 px-4">
                  {other.map((inv) => (
                    <InvoiceCard key={inv.id} invoice={inv} paying={false} onPay={() => {}} />
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}

function InvoiceCard({ invoice, paying, onPay }: { invoice: Invoice; paying: boolean; onPay: () => void }) {
  return (
    <div className="bg-bg-input rounded-2xl p-4 border border-divider">
      <div className="flex items-start gap-3">
        <span className="text-2xl shrink-0">🧾</span>
        <div className="flex-1 min-w-0">
          <p className="text-txt-primary text-sm font-medium truncate">{invoice.description || 'Invoice'}</p>
          <p className="text-txt-meta text-xs mt-0.5">
            Expires {new Date(invoice.expires_at).toLocaleDateString()}
          </p>
          <p className={`text-xs mt-1 font-medium ${statusColor(invoice.status)}`}>
            {statusLabel(invoice.status)}
          </p>
        </div>
        <div className="shrink-0 text-right">
          <p className="text-txt-primary font-semibold text-sm">{invoice.price_stars} ⭐</p>
          {invoice.status === 'pending' && (
            <button
              onClick={onPay}
              disabled={paying}
              className="mt-1.5 bg-accent hover:bg-blue-500 text-white text-xs font-medium px-3 py-1.5 rounded-lg transition-colors disabled:opacity-40"
            >
              {paying ? '…' : 'Pay'}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
